<template>
  <dd-modal
      :show-cancel="true"
      @cancel="$emit('close')"
      v-bind="$attrs"
      :visible="visible"
  >
    <dd-header-card
        :header="'Notitie ' + (isEditing ? 'bewerken' : 'aanmaken')"
        data-dusk="create-or-edit-notes-modal"
    >
      <dd-form :valid-on-init="true" @submit="onSubmit">
          <dd-step title="Basisgegevens">
            <dd-form-grid>
              <dd-form-grid-item>
                <dd-select
                    data-dusk="create-for-customer"
                    @click.prevent=""
                    title="Aanmaken voor"
                    v-model="note.customer_id"
                    :options="familyOptions"
                ></dd-select>
              </dd-form-grid-item>
              <dd-form-grid-item>
                <dd-input :value="createNoteIn" title="Aanmaken in" data-dusk="create-note-in" :disabled="true"></dd-input>
              </dd-form-grid-item>
              <dd-form-grid-item>
                <dd-switch v-model="note.visible_for_customer" title="Is zichtbaar voor klant?"></dd-switch>
              </dd-form-grid-item>
            </dd-form-grid>
          </dd-step>
          <dd-step title="Onderwerp en inhoud">
            <dd-form-grid>
              <dd-form-grid-item>
                <dd-input v-model='note.title' title="Onderwerp" rules="required" data-dusk="title"></dd-input>
              </dd-form-grid-item>
              <dd-form-grid-item>
                <dd-textarea v-model='note.content' title="Inhoud" rules="required" data-dusk="content"></dd-textarea>
              </dd-form-grid-item>
              <validation-provider slim rules="required" v-slot="validationProps">
                <input :value="note.user_id" type="hidden"/>
              </validation-provider>
            </dd-form-grid>
          </dd-step>
      </dd-form>
    </dd-header-card>
  </dd-modal>
</template>

<script>
import moment from 'moment';
import noteApi from '../../../api/note';
import { mapState, mapActions, mapGetters, mapMutations  } from 'vuex';
import DateFormatsEnum from "../../../new_design_components/enums/DateFormatsEnum";
import DOMPurify from "dompurify";

export default {
  name: "CreateOrEditNotesModal",
  props: {
    visible: Boolean,
    noteId: {
      required: false,
      type: [Number, String],
    }
  },
  emits: ['created', 'updated'],
  data() {
    return {
      userLoaded: false,
      initialNote: null,
      note: {
        title: '',
        content: '',
        datetime: moment().format(DateFormatsEnum.ISO8601),
        customer_id: this.customerId,
        visible_for_customer: false,
        category: null,
        instanceType: null,
        instanceId: null,
        user_id: null,
      }
    }
  },
  created() {
    this.getFamily();
    this.userLoaded = false;
    this.getCategoryName();

    // Fill some derived fields
    this.getMe().then(() => {
      this.note = {
        ...this.note,
        user_id: this.me?.id,
      };
      this.userLoaded = true;
      this.note.customer_id = this.customerId;
      this.setNoteType();
      this.initialNote = {...this.note};
    });
  },
  computed: {
    isEditing() {
      return !!this.noteId;
    },
    createNoteIn() {
      if (this.isInstancePage) {
        return this.instance.name;
      }
      else if (this.isCategoryPage) {
        return this.category.name;
      }
    },
    ...mapState({
      category: 'category',
      instance: 'instance',
      category_id: 'category_id',
      me: 'me',
    }),
    ...mapState(`$_customer_page`, {
      customerId: 'customerId',
      showCreateNoteModal: 'showCreateNoteModal',
    }),
    ...mapGetters(`$_customer_page`, {
      familyOptions: 'familyOptions',
    }),
    ...mapGetters({
      isInstancePage: 'isInstancePage',
      isCategoryPage: 'isCategoryPage',
    }),
  },
  watch: {
    noteId(id) {
      if (this.isEditing && this.visible) {
        noteApi.find(id).then((response) => {
          this.note = {...this.note, ...response.data };
        });
      }
    },
    showCreateNoteModal(visible) {
      if(!visible) {
        this.onClose();
      }
    }
  },
  methods: {
    onClose() {
      this.setEditNoteId(undefined);
      this.note = this.initialNote;
      this.resetForm();
      this.$emit('close');
    },
    setNoteType() {
      if (this.isInstancePage) {
        this.note = {
          ...this.note,
          category: this.category_id,
          instanceId: this.instance.id,
          instanceType: this.instance.type,
        };
      }
      else if (this.isCategoryPage) {
        this.note = {
          ...this.note,
          category: this.category_id,
        }
      }
      else {
        this.noCategoryOrInstanceFoundError();
      }
    },
    onUpdated() {
      this.$emit('updated');
    },
    onCreated() {
      this.$emit('created');
    },
    noCategoryOrInstanceFoundError() {
      throw new Error("Can't create note because there is no category or class instance provided for this page.");
    },
    ...mapActions({
      getCategoryName: 'getCategoryName',
      getMe: 'getMe',
    }),
    ...mapMutations(`$_customer_page`, {
      setEditNoteId: "SET_EDIT_NOTE_ID",
      setShowCreateNoteModal: 'SET_SHOW_CREATE_NOTE_MODAL',
    }),
    ...mapActions(`$_customer_page`, {
      getFamily: 'getFamily',
      getNotesCount: 'getNotesCount',
    }),
    ...mapMutations(`$_notes_drawer`, {
      setReloadNotes: 'SET_RELOAD_NOTES',
    }),
    onSuccess(response) {
      this.getNotesCount();
      this.setShowCreateNoteModal(false);
      this.setReloadNotes(true);
      this.resetForm();
      this.notificationsManager.success(response);
    },
    onSubmit() {
      this.note = {
        ...this.note,
        title: DOMPurify.sanitize(this.note.title),
        content: DOMPurify.sanitize(this.note.content)
      };

      if (this.isEditing) {
        noteApi.update(this.note).then(this.onUpdated).then((response) => {
          this.setEditNoteId(undefined);
          this.onSuccess(response);
        });
      }
      else if (this.isInstancePage) {
        noteApi.createByInstance(this.note).then(this.onCreated).then((response) => {
          this.onSuccess(response);
        });
      }
      else if (this.isCategoryPage) {
        noteApi.create(this.note).then(this.onCreated).then((response) => {
          this.onSuccess(response);
        });
      }
      else {
        this.noCategoryOrInstanceFoundError();
      }
    },
    resetForm() {
      this.note.title = '';
      this.note.content = '';
      this.note.datetime = moment().format(DateFormatsEnum.ISO8601);
      this.note.customer_id = this.customerId;
      this.note.visible_for_customer = false;
      this.note.category = null;
      this.note.instanceType = null;
      this.note.instanceId = null;
      this.note.user_id = null;
    }
  },
};
</script>

<style lang="scss" scoped>
@import "~@/_default.scss";
</style>
